// Shared helpers for the export system. Renderers (csvExport/pdfExport/pptxExport)
// receive already-resolved sections plus a meta object built here.
import { PAGE_DEFAULTS } from './exportDefaults';
import { formatFullNumber } from '../../utils/format';

export { hexToRgb, hexToPptx } from './exportDefaults';
export { resolveSections } from './exportSections';

const PAGE_TYPE_LABELS = {
  reports: 'Ad Performance',
  analytics: 'Web Analytics',
  dashboard: 'Dashboard',
};

const DATE_RANGE_PRESETS = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  '90d': 'Last 90 Days',
  '6m': 'Last 6 Months',
  '12m': 'Last 12 Months',
};

// Date-only strings (YYYY-MM-DD) are UTC day buckets — format them in UTC so the
// day never shifts. Date instances and full timestamps format in local time so DB
// bucket timestamps and local-midnight picker Dates render the intended calendar day.
const isDateOnly = (raw) => typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw);
const pad2 = (n) => String(n).padStart(2, '0');

const formatDateLabel = (raw) => {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw === null || raw === undefined ? '' : String(raw);
  const options = { month: 'short', day: 'numeric', year: 'numeric' };
  if (isDateOnly(raw)) options.timeZone = 'UTC';
  return d.toLocaleDateString('en-US', options);
};

const formatDateTimeLabel = (raw) => {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw === null || raw === undefined ? '' : String(raw);
  const hours = isDateOnly(raw) ? d.getUTCHours() : d.getHours();
  return `${formatDateLabel(raw)} ${pad2(hours)}:00`;
};

const formatDuration = (raw) => {
  const total = Math.round(Number(raw) || 0);
  if (total >= 3600) {
    const h = Math.floor(total / 3600);
    const m = Math.round((total % 3600) / 60);
    return `${h}h ${m}m`;
  }
  if (total >= 60) {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}m ${s}s`;
  }
  return `${total}s`;
};

// Display string for PDF/PPTX rendering
export const formatValue = (raw, type) => {
  switch (type) {
    case 'number':
      return formatFullNumber(raw);
    case 'percent': {
      const n = parseFloat(raw);
      return `${(Number.isFinite(n) ? n : 0).toFixed(2)}%`;
    }
    case 'duration':
      return formatDuration(raw);
    case 'date':
      return formatDateLabel(raw);
    case 'datetime':
      return formatDateTimeLabel(raw);
    default:
      return raw === null || raw === undefined ? '' : String(raw);
  }
};

// Raw machine value for CSV cells (numbers stay numbers — never coerce 0 to '')
export const csvValue = (raw, type) => {
  switch (type) {
    case 'number': {
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    }
    case 'percent': {
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : 0;
    }
    case 'duration':
      return Math.round(Number(raw) || 0);
    case 'date': {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw === null || raw === undefined ? '' : String(raw);
      if (isDateOnly(raw)) return raw;
      return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    }
    case 'datetime': {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw === null || raw === undefined ? '' : String(raw);
      if (isDateOnly(raw)) return `${raw} 00:00`;
      return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:00`;
    }
    default:
      return raw === null || raw === undefined ? '' : raw;
  }
};

export const formatPeriodLabel = (dateStr, granularity) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr === null || dateStr === undefined ? '' : String(dateStr);
  if (granularity === 'month') {
    const options = { month: 'long', year: 'numeric' };
    if (isDateOnly(dateStr)) options.timeZone = 'UTC';
    return d.toLocaleDateString('en-US', options);
  }
  const label = formatDateLabel(dateStr);
  return granularity === 'week' ? `Week of ${label}` : label;
};

export const dateRangeLabel = (dateRange) => {
  if (typeof dateRange === 'string' && DATE_RANGE_PRESETS[dateRange]) {
    return DATE_RANGE_PRESETS[dateRange];
  }
  if (dateRange && typeof dateRange === 'object' && (dateRange.start || dateRange.end)) {
    return `${formatDateLabel(dateRange.start)} – ${formatDateLabel(dateRange.end)}`;
  }
  return String(dateRange || '');
};

// Load an image URL as a PNG data URL. Resolves null on any failure —
// renderers must handle a null logo.
export const loadImageAsDataUrl = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      return { dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (e) {
    return null;
  }
};

export const buildMeta = ({ config, pageType, dateRange, granularity, logo, entity }) => {
  const pageDefaults = PAGE_DEFAULTS[pageType] || {};
  const companyName = config.branding?.companyName || 'StatFalcon';
  const now = new Date();
  const entitySuffix = entity
    ? ` — ${String(entity.type).charAt(0).toUpperCase() + String(entity.type).slice(1)}: ${entity.name}`
    : '';
  return {
    title: config.text?.title || pageDefaults.title || 'Report',
    subtitle: `${config.text?.subtitle || pageDefaults.subtitle || ''}${entitySuffix}`,
    footerText: config.text?.footerText || `Generated by ${companyName}`,
    companyName,
    colors: {
      primary: config.colors?.primary || '#6366f1',
      accent: config.colors?.accent || '#d946ef',
    },
    logo: logo || null,
    dateRangeLabel: dateRangeLabel(dateRange),
    generatedAt: `${now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`,
    granularity: granularity || null,
    pageTypeLabel: PAGE_TYPE_LABELS[pageType] || 'Report',
  };
};

export const downloadBlob = (filename, blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
